import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ShieldCheck, ListChecks, Radar, QrCode, Gauge, ArrowRight } from "lucide-react";
import PublicShell, { usePublicSettings } from "./PublicShell";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FEATURE_ICONS = [Gauge, ListChecks, Radar, QrCode, ShieldCheck];

export default function Home() {
  const { t } = useTranslation();
  const settings = usePublicSettings();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);

  const home = settings?.home || {};

  useEffect(() => {
    client.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const value = q.trim();
    if (!value) return;
    setState("loading");
    setError("");
    try {
      const { data } = await client.get("/verify", { params: { q: value } });
      if (data.found) navigate(`/act/${data.token}`);
      else setState("notfound");
    } catch {
      setError(t("verify.error"));
      setState("idle");
    }
  };

  const hero = home.hero || {};
  const how = home.how || {};
  const features = home.features || {};
  const statsCfg = home.stats || {};
  const steps = t("how.steps", { returnObjects: true }) || [];
  const cards = t("features.cards", { returnObjects: true }) || [];

  return (
    <PublicShell settings={settings}>
      {hero.enabled !== false && (
        <section className="border-b border-neutral-100">
          <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-5">
              <ShieldCheck className="w-4 h-4" style={{ color: "var(--ci-accent)" }} />
              {t("hero.badge")}
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-4" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="text-neutral-600 text-base mb-10 max-w-xl" data-testid="hero-subtitle">{t("hero.subtitle")}</p>

            <form onSubmit={submit} className="space-y-4 max-w-xl">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input data-testid="verify-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hero.placeholder")} className="h-14 pl-10 text-base rounded-lg border-neutral-300" autoFocus />
              </div>
              <Button type="submit" data-testid="verify-submit" disabled={state === "loading"} className="h-12 w-full sm:w-auto px-8 rounded-lg font-semibold active:scale-[0.98] transition-transform" style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
                {state === "loading" ? t("common.checking") : t("common.check")}
              </Button>
            </form>

            {error && <p className="mt-6 text-sm text-red-600" data-testid="verify-error">{error}</p>}
            {state === "notfound" && (
              <div className="mt-8 border border-neutral-200 rounded-lg p-6 bg-neutral-50 max-w-xl" data-testid="verify-notfound">
                <p className="font-medium">{t("verify.notfoundTitle")}</p>
                <p className="text-sm text-neutral-600 mt-1">{t("verify.notfoundText")}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {how.enabled !== false && (
        <section className="border-b border-neutral-100 bg-neutral-50/60">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <h2 className="text-lg font-semibold tracking-tight mb-8" data-testid="how-title">{t("how.title")}</h2>
            <div className="grid gap-8 sm:grid-cols-3">
              {steps.map((s, i) => (
                <div key={i} data-testid={`how-step-${i}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center font-semibold mb-4" style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>{i + 1}</div>
                  <h3 className="font-medium mb-1.5">{s.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {features.enabled !== false && (
        <section className="border-b border-neutral-100">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <h2 className="text-lg font-semibold tracking-tight mb-8" data-testid="features-title">{t("features.title")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {cards.map((c, i) => {
                const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                return (
                  <div key={i} data-testid={`feature-card-${i}`} className="border border-neutral-200 rounded-lg p-5 hover:border-neutral-300 transition-colors">
                    <Icon className="w-5 h-5 mb-3" style={{ color: "var(--ci-accent)" }} />
                    <h3 className="font-medium mb-1">{c.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{c.text}</p>
                  </div>
                );
              })}
            </div>
            <Link to="/decoder" className="inline-flex items-center gap-1.5 text-sm font-medium mt-8 hover:gap-2.5 transition-all" data-testid="features-decoder-link">
              {t("features.decoderLink")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {statsCfg.enabled !== false && stats && (
        <section className="border-b border-neutral-100 bg-neutral-900">
          <div className="mx-auto max-w-3xl px-6 py-16 text-white">
            <h2 className="text-lg font-semibold tracking-tight mb-8 text-white/90" data-testid="stats-title">{t("stats.title")}</h2>
            <div className="grid grid-cols-3 gap-6">
              <div data-testid="stat-acts">
                <div className="text-4xl font-semibold tracking-tighter" style={{ color: "var(--ci-accent)" }}>{stats.acts}</div>
                <div className="text-sm text-white/60 mt-1">{t("stats.acts")}</div>
              </div>
              <div data-testid="stat-brands">
                <div className="text-4xl font-semibold tracking-tighter" style={{ color: "var(--ci-accent)" }}>{stats.brands}</div>
                <div className="text-sm text-white/60 mt-1">{t("stats.brands")}</div>
              </div>
              <div data-testid="stat-verifications">
                <div className="text-4xl font-semibold tracking-tighter" style={{ color: "var(--ci-accent)" }}>{stats.verifications}</div>
                <div className="text-sm text-white/60 mt-1">{t("stats.verifications")}</div>
              </div>
            </div>
          </div>
        </section>
      )}
    </PublicShell>
  );
}
